
import {defineComponent} from "vue";
import {CodeDescriptionRestDto, MaterialSymbolRestDto} from "@/api/pharma-cms-content-block/models";
import PageLinkPicker from "@/components/UI/PageLinkPicker.vue";
import {PageLinkPayload} from "@/models/payload/PageLinkPayload";
import {PageLinkTypeEnum} from "@/models/enum/PageLinkTypeEnum";
import {MultilingualRestDtoModel} from "@/models/MultilingualRestDtoModel";
import {CbStructureMixin} from "@/components/layouts/content-pages/wysiwyg/structures/CbStructureMixin";
import {NormalContentBlockRestService} from "@/services/rest/cms-content-block/NormalContentBlockRestService";
import {FindNormalContentBlockPayload} from "@/models/payload/FindNormalContentBlockPayload";
import {CreateNormalContentBlockPayload} from "@/models/payload/CreateNormalContentBlockPayload";
import {SaveNormalContentBlockPayload} from "@/models/payload/SaveNormalContentBlockPayload";
import {LinkTargetEnum} from "@/models/enum/LinkTargetEnum";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import BaseSpinner from "@/components/UI/Bootstrap/BaseSpinner.vue";
import {SymbolContentRestDtoModel} from "@/models/api/pharma-cms-content-block/SymbolContentRestDtoModel";
import {SymbolTargetEnum} from "@/api/enums/symbol-target-enum";
import Subtitle from "@/components/UI/Subtitle.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import PreviewSymbol from "@/components/UI/PreviewSymbol.vue";
import MaterialSymbolInputComponent from "@/components/UI/MaterialSymbolInputComponent.vue";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";

export default defineComponent({
  name: "CbSymbol",
  extends: CbStructureMixin,
  components: {
    MaterialSymbolInputComponent,
    PreviewSymbol,
    Subtitle,
    InputSelect,
    PageLinkPicker,
    BaseSpinner
  },
  data: () => ({
    contentBlock: SymbolContentRestDtoModel.createWithDefaults(),
    pageLinkPayload: PageLinkPayload.createWithDefaults(),
    enumHorizontalPosition: [] as CodeDescriptionRestDto[]
  }),
  mounted() {
    this.initialize();
  },
  watch: {
    contentBlock() {
      const externalUrl = this.contentBlock.symbol.symbol_url?.nl ?? null;
      const pageId = this.contentBlock.symbol.symbol_page_id?.nl ?? null;
      const target = this.contentBlock.symbol.symbol_target ?? LinkTargetEnum.SELF;

      this.pageLinkPayload = new PageLinkPayload(externalUrl, pageId, target.toString() as LinkTargetEnum);
    },
  },
  methods: {
    async find(payload: FindNormalContentBlockPayload): Promise<SymbolContentRestDtoModel> {
      return await NormalContentBlockRestService.getInstance().findSymbol(payload.type, payload.id);
    },
    async create(payload: CreateNormalContentBlockPayload): Promise<string> {
      return await NormalContentBlockRestService.getInstance().createSymbol(payload.cb as SymbolContentRestDtoModel, payload.type);
    },
    async save(payload: SaveNormalContentBlockPayload): Promise<void> {
      await NormalContentBlockRestService.getInstance().saveSymbol(payload.cb as SymbolContentRestDtoModel, payload.type, payload.id);
    },
    async initialize(): Promise<void> {
      this.enumHorizontalPosition = await EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.OBJECT_HORIZONTAL_POSITION, DpModuleEnum.CMS);
    },
    getDefaultCbModel(): SymbolContentRestDtoModel {
      return SymbolContentRestDtoModel.createWithDefaults();
    },
    onMaterialSymbolUpdate(payload: MaterialSymbolRestDto): void {
      this.contentBlock.symbol.material_symbol = payload;
      this.onChange();
    },
    onPageLinkPickerChanged(payload: PageLinkPayload): void {
      if (payload.type === PageLinkTypeEnum.NONE) {
        this.contentBlock.symbol.symbol_url = undefined;
        this.contentBlock.symbol.symbol_page_id = undefined;
        this.contentBlock.symbol.symbol_target = undefined;
      } else if (payload.type === PageLinkTypeEnum.URL) {
        this.contentBlock.symbol.symbol_url = new MultilingualRestDtoModel(null, null, null, payload.external_url);
        this.contentBlock.symbol.symbol_page_id = undefined;
        this.contentBlock.symbol.symbol_target = payload.target.toString() as SymbolTargetEnum;
      } else if (payload.type === PageLinkTypeEnum.PAGE) {
        this.contentBlock.symbol.symbol_url = undefined;
        this.contentBlock.symbol.symbol_page_id = new MultilingualRestDtoModel(null, null, null, payload.page_id);
        this.contentBlock.symbol.symbol_target = payload.target.toString() as SymbolTargetEnum;
      }

      this.onChange();
    },
    pasteCbMetadata(): void {
      // Reassigning contentBlock (instead of mutating it) is what fires the contentBlock watcher.
      const cbClone = SymbolContentRestDtoModel.cloneFrom(this.contentBlock);

      cbClone.cloneMetadataFrom(this.getClipboardContentBlock(this.cbType) as SymbolContentRestDtoModel);
      this.contentBlock = cbClone;
    },
  }
});
